import {memo, useRef} from 'react';

import {
  CopyToClipboard,
  EllipsisTooltip,
  Portal,
} from '@macmillanplatform/ui-core';
import {
  useAutoPosition,
  useClickOutside,
  useVisibility,
} from '@macmillanplatform/ui-core/hooks';

import {OrderItem} from '@/types';

import {selectOrder} from '../../../../helpers/selected-order';

import DateFormatter from '../date-formatter';

import './index.css';

interface PoNumberFormatterProps {
  cellData: string;
  poNumber: string;
  orderDate: string;
}

const PoNumberFormatter = memo((props: PoNumberFormatterProps) => {
  const {cellData, poNumber, orderDate} = props;

  const handleSelect = () => selectOrder({poNumber, orderDate});

  return (
    <div className="PoNumberFormatter">
      <EllipsisTooltip className="PoNumber__cell" onClick={handleSelect}>
        <>{cellData || ''}</>
      </EllipsisTooltip>
    </div>
  );
});

const InvoiceNumber = memo(({invoiceNumber}: {invoiceNumber: string}) => (
  <CopyToClipboard
    text={invoiceNumber}
    tooltip="Click to copy invoice number"
  />
));

interface InvoiceNumbersPopoverProps {
  anchor: HTMLElement | null;
  invoiceNumbers: string[];
  onClose: () => void;
}

const InvoiceNumbersPopover = ({
  anchor,
  invoiceNumbers,
  onClose,
}: InvoiceNumbersPopoverProps) => {
  const popoverRef = useClickOutside<HTMLDivElement>(event => {
    if (event.target instanceof Node && anchor?.contains(event.target)) {
      return;
    }

    onClose();
  });

  const {styles} = useAutoPosition<HTMLDivElement>(
    {
      parentElement: anchor,
      bodyRef: popoverRef,
      align: 'bottom',
      isDropdown: true,
    },
    [anchor]
  );

  return (
    <Portal>
      <div
        ref={popoverRef}
        className="InvoiceNumbers__popover"
        style={{position: 'absolute', ...styles}}
      >
        <span className="InvoiceNumbers__popover-title">
          Invoice Numbers ({invoiceNumbers.length})
        </span>
        <ul className="InvoiceNumbers__popover-list">
          {invoiceNumbers.map((invoiceNumber, idx) => (
            <li
              key={`${invoiceNumber}-${idx}`}
              className="InvoiceNumbers__popover-item"
            >
              <InvoiceNumber invoiceNumber={invoiceNumber} />
            </li>
          ))}
        </ul>
      </div>
    </Portal>
  );
};

const InvoiceNumbersFormatter = memo(
  ({cellData = []}: {cellData?: string[]}) => {
    const invoiceNumbers = Array.isArray(cellData) ? cellData : [];
    const {isVisible, toggle, hide} = useVisibility();
    const anchorRef = useRef<HTMLButtonElement>(null);

    if (!invoiceNumbers.length) {
      return null;
    }

    const [firstInvoiceNumber, ...restInvoiceNumbers] = invoiceNumbers;
    const hiddenCount = restInvoiceNumbers.length;

    return (
      <div className="InvoiceNumbers__cell">
        <InvoiceNumber invoiceNumber={firstInvoiceNumber} />
        {!!hiddenCount && (
          <>
            <button
              ref={anchorRef}
              type="button"
              className="InvoiceNumbers__more"
              aria-expanded={isVisible}
              onClick={event => {
                event.stopPropagation();
                toggle(event);
              }}
            >
              +{hiddenCount} more
            </button>
            {isVisible && (
              <InvoiceNumbersPopover
                anchor={anchorRef.current}
                invoiceNumbers={restInvoiceNumbers}
                onClose={hide}
              />
            )}
          </>
        )}
      </div>
    );
  }
);

export const getOrdersFormatters = () => ({
  order_date: ({cellData}: {cellData: string}) => (
    <DateFormatter date={cellData} />
  ),
  po_number: ({cellData, rowData}: {cellData: string; rowData: OrderItem}) => (
    <PoNumberFormatter
      cellData={cellData}
      poNumber={rowData.po_number}
      orderDate={rowData.order_date}
    />
  ),
  invoice_numbers: ({cellData}: {cellData: string[]}) => (
    <InvoiceNumbersFormatter cellData={cellData} />
  ),
});
